/**
 * 0.36.648 / 0.36.720 / 0.36.721 — Shared repeater helpers.
 *
 * As of Ship A (0.36.720) the repeater was rebuilt to a
 * template + entries model. As of Ship B (0.36.721) the runtime
 * uses these helpers to manipulate draft state (the currently-
 * visible template inputs) and committed entries (an array of
 * objects stored under the repeater's element_id).
 *
 * Data shape:
 *   ctx.values[repName]                       -> array of entry objects
 *   ctx.values[`${repName}.__draft.<field>`]  -> draft input value
 *   ctx.values[`${repName}.__editing`]        -> integer entry index
 *                                                 currently being edited,
 *                                                 or -1 for "adding new"
 *
 * Committed entries carry only the child field names as keys
 * (`{name: 'Sam', relationship: 'spouse'}`); nested repeaters and
 * tables are disallowed in v1 so no recursive shape.
 */

// ---- Element scoping ----

/**
 * Deep-clone an element with `prefix` prepended to `name` /
 * `element_id` on itself and every descendant. Also rewrites
 * `filter_by_element` so cascading-filter references between
 * siblings inside the same scope keep working. React `id` is
 * also namespaced so two copies of the same template don't
 * collide on React keys.
 *
 * Used by the runtime to scope template inputs under
 * `<repName>.__draft.<field>` so draft state never collides
 * with any top-level form field of the same name.
 */
export function rewriteElementIdsRecursive(el, prefix) {
  if (!el || typeof el !== 'object') return el;
  const cloned = { ...el };
  cloned.id = `${prefix}${el.id || ''}`;
  const origName = cloned.name || cloned.element_id;
  if (origName) {
    cloned.name = prefix + origName;
    if (cloned.element_id) cloned.element_id = prefix + origName;
  }
  if (cloned.filter_by_element) {
    cloned.filter_by_element = prefix + cloned.filter_by_element;
  }
  if (Array.isArray(cloned.children)) {
    cloned.children = cloned.children.map(c => rewriteElementIdsRecursive(c, prefix));
  }
  if (Array.isArray(cloned.columns)) {
    cloned.columns = cloned.columns.map(col => {
      if (!col || typeof col !== 'object') return col;
      const nc = { ...col };
      if (Array.isArray(nc.children)) {
        nc.children = nc.children.map(c => rewriteElementIdsRecursive(c, prefix));
      }
      return nc;
    });
  }
  return cloned;
}

// ---- Template field introspection ----

// Element types whose value should be captured as an entry field.
// Layout containers (column_group, line_group) are walked but not
// captured themselves. Display-only elements (title, hint, markdown,
// image, button) don't hold values so they're skipped. Nested
// repeaters and tables are disallowed in v1 (see behavior note in
// blankElement) but also skipped defensively here.
const INPUT_TYPES = new Set([
  'text', 'password', 'textarea', 'number', 'email', 'phone', 'date',
  'dropdown', 'multiselect', 'radio', 'checkbox', 'file',
]);

function _walkTemplate(template, cb) {
  if (!Array.isArray(template)) return;
  for (const n of template) {
    if (!n || typeof n !== 'object') continue;
    const eid = n.element_id || n.name;
    if (eid && INPUT_TYPES.has(n.type)) cb(n, eid);
    if (Array.isArray(n.children)) _walkTemplate(n.children, cb);
    if (Array.isArray(n.columns)) {
      for (const col of n.columns) {
        if (col && Array.isArray(col.children)) _walkTemplate(col.children, cb);
      }
    }
  }
}

/**
 * Return the ordered list of input-like field names in the template.
 * Order follows the template's structure — used for building entry
 * objects (Add) and label/value entry display (deterministic order).
 */
export function collectTemplateFieldNames(template) {
  const out = [];
  _walkTemplate(template, (_node, eid) => out.push(eid));
  return out;
}

/**
 * Return { eid: labelForDisplay } for every input-like field in the
 * template. Used by the entry card to render the left column.
 * Falls back to the eid when the element has no explicit label.
 */
export function collectTemplateFieldLabels(template) {
  const out = {};
  _walkTemplate(template, (node, eid) => {
    out[eid] = (node.label && String(node.label).trim()) || eid;
  });
  return out;
}

/**
 * Return the ordered list of REQUIRED field names — subset of
 * collectTemplateFieldNames that admin marked required. Used to
 * validate draft on Add / Save.
 */
export function collectRequiredFieldNames(template) {
  const out = [];
  _walkTemplate(template, (node, eid) => {
    if (node.required) out.push(eid);
  });
  return out;
}

// ---- Draft state helpers ----

/**
 * Read the current draft state as { fieldName: value } object.
 * Missing / undefined values render as empty strings.
 */
export function readDraft(values, repName, fieldNames) {
  const prefix = `${repName}.__draft.`;
  const out = {};
  for (const fname of fieldNames) {
    const raw = values[`${prefix}${fname}`];
    out[fname] = raw == null ? '' : raw;
  }
  return out;
}

/**
 * Clear every draft field back to empty. Uses updateValue for each
 * so ruleState / validation see the change.
 */
export function clearDraft(updateValue, repName, fieldNames) {
  const prefix = `${repName}.__draft.`;
  for (const fname of fieldNames) {
    updateValue(`${prefix}${fname}`, '');
  }
}

/**
 * Copy an entry object's values into the draft fields, for Edit.
 */
export function loadEntryIntoDraft(updateValue, repName, fieldNames, entry) {
  const prefix = `${repName}.__draft.`;
  for (const fname of fieldNames) {
    const v = entry && Object.prototype.hasOwnProperty.call(entry, fname)
      ? entry[fname] : '';
    updateValue(`${prefix}${fname}`, v);
  }
}

/**
 * Validate that all required draft fields have non-empty values.
 * Returns { valid, missing: [fieldName...] }. Empty = '' / null /
 * undefined / [] (for multiselect).
 */
export function validateDraft(values, repName, template) {
  const required = collectRequiredFieldNames(template);
  const prefix = `${repName}.__draft.`;
  const missing = [];
  for (const fname of required) {
    const v = values[`${prefix}${fname}`];
    const empty = v == null || v === ''
      || (Array.isArray(v) && v.length === 0);
    if (empty) missing.push(fname);
  }
  return { valid: missing.length === 0, missing };
}

// ---- Entry state helpers ----

/** Read committed entries as an array (empty array if unset / wrong shape). */
export function readEntries(values, repName) {
  const raw = values[repName];
  return Array.isArray(raw) ? raw : [];
}

/**
 * Read the currently-editing entry index. -1 means "adding new"
 * (default state — no entry is being edited).
 */
export function readEditingIdx(values, repName) {
  const raw = values[`${repName}.__editing`];
  return Number.isInteger(raw) ? raw : -1;
}

/**
 * Format an entry-field value for the read-only label/value display.
 * Handles the shapes citizen inputs commonly produce.
 */
export function formatEntryValue(value) {
  if (value == null || value === '') return '';
  if (Array.isArray(value)) return value.filter(v => v != null && v !== '').join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}
